/**
 * Wat er met een geregistreerde betaling wel en niet mag, bij inboeken én uitboeken.
 *
 * <p>Eén plek, want het is dezelfde regel aan allebei de kanten van de balie en een regel die op twee
 * plekken staat gaat op één plek mee met de volgende wijziging.
 */

/** Centen vergelijken, niet centen-met-restjes: een totaal is een optelling van afgeronde bedragen. */
const SPELING = 0.005;

export type BetalingFout =
  /** Er is geld geregistreerd in de verkeerde richting — zie {@link betalingOordeel}. */
  | "TEGENGESTELD"
  /** Er is meer geregistreerd dan er te betalen staat. */
  | "TEVEEL";

export interface BetalingOordeel {
  /** De reden dat je niet verder kunt, of null als er niets aan de hand is. */
  fout: BetalingFout | null;
  /** Wat er na deze betaling open blijft staan. Nul is klaar; een bedrag vraagt om een bevestiging. */
  openstaand: number;
  /** Of er een bedrag open blijft staan dat de gebruiker moet bevestigen. */
  restant: boolean;
}

/**
 * Het oordeel over een betaling.
 *
 * <p><b>Een negatief bedrag mag alleen bij een negatieve factuur.</b> Geld terugbetalen kan echt
 * gebeuren — een retour na plaatsing waarbij de eigenaar geld terugkrijgt — maar dan is het
 * factuurtotaal zelf ook negatief. Een negatieve betaling op een positieve factuur is altijd een
 * typefout, en andersom net zo (Maarten, 2026-10-24). Daarom kijkt deze regel naar het téken en niet
 * naar "groter dan nul": dezelfde richting als het totaal, en niet verder dan het totaal.
 *
 * <p><b>Minder betalen mag wél.</b> Een deel nu en de rest later is een gewone afspraak; die wordt niet
 * geblokkeerd maar bevestigd, zodat niemand per ongeluk een openstaand bedrag laat staan dat hij niet
 * bedoelde.
 *
 * @param teBetalen het factuurtotaal; negatief als het asiel geld teruggeeft
 * @param geregistreerd wat er aan betalingen is ingevuld, inclusief een bedrag dat later volgt
 */
export function betalingOordeel(teBetalen: number, geregistreerd: number): BetalingOordeel {
  const openstaand = afgerond(teBetalen - geregistreerd);

  if (Math.abs(teBetalen) < SPELING) {
    // Niets te betalen: dan hoort er ook niets geregistreerd te worden, in welke richting dan ook.
    return {
      fout: Math.abs(geregistreerd) < SPELING ? null : "TEVEEL",
      openstaand: 0,
      restant: false,
    };
  }

  const zelfdeRichting = Math.sign(geregistreerd) === Math.sign(teBetalen) || Math.abs(geregistreerd) < SPELING;
  if (!zelfdeRichting) {
    return { fout: "TEGENGESTELD", openstaand, restant: false };
  }
  if (Math.abs(geregistreerd) - Math.abs(teBetalen) > SPELING) {
    return { fout: "TEVEEL", openstaand, restant: false };
  }
  return { fout: null, openstaand, restant: Math.abs(openstaand) > SPELING };
}

function afgerond(bedrag: number): number {
  return Math.round(bedrag * 100) / 100;
}
